import type { PageBackground } from '@/components/canvas/document/publicTypes';
import { loadImage } from '@/components/canvas/rendering/export/loadImage';
import type { GetImageProxyUrl } from '@/components/canvas/rendering/export/types';
import { parseGradientToCanvasGradient } from '@/components/canvas/rendering/export/gradient';

export async function drawBackground(
  ctx: CanvasRenderingContext2D,
  background: PageBackground,
  width: number,
  height: number,
  getImageProxyUrl?: (src: string) => Promise<string | null>
): Promise<void> {
  if (background.type === 'color') {
    ctx.fillStyle = background.value ?? '#ffffff';
    ctx.fillRect(0, 0, width, height);
  } else if (background.type === 'gradient' && background.value) {
    const grad = parseGradientToCanvasGradient(ctx, background.value, width, height);
    if (grad) {
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
    }
  } else if (background.type === 'image' && background.value) {
    ctx.fillStyle = background.backgroundColor ?? '#ffffff';
    ctx.fillRect(0, 0, width, height);
    try {
      const img = await loadImage(background.value, getImageProxyUrl);
      const posX = (background.imagePositionX ?? 50) / 100;
      const posY = (background.imagePositionY ?? 50) / 100;
      // cover: scale to fill, offset by position
      const imgRatio = img.width / img.height;
      const pageAspectRatio = width / height;
      let sw: number, sh: number, sx: number, sy: number;
      if (imgRatio > pageAspectRatio) {
        sh = img.height;
        sw = Math.round(img.height * pageAspectRatio);
        sx = Math.round((img.width - sw) * posX);
        sy = 0;
      } else {
        sw = img.width;
        sh = Math.round(img.width / pageAspectRatio);
        sx = 0;
        sy = Math.round((img.height - sh) * posY);
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
    } catch {
      // image failed to load, background color already drawn
    }
  } else if (background.type === 'transparent') {
    // leave transparent (checkerboard is display-only)
  } else {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
}
